import { Button, DialogFooter, Field, FieldGroup, FieldLabel, Modal, Switch, Textarea } from "@peer-start/ui/reducate";
import { CircleHelp } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { noteSubjectValue, parseNoteSubject, type StartingTeacherNoteSubject } from "../data/starting-teacher-route.js";
import { StartingTeacherNoteSubjectField } from "./StartingTeacherNoteSubjectField.js";

export function StartingTeacherNoteModal({
  open,
  subject,
  onClose,
  onSave,
}: {
  open: boolean;
  subject: StartingTeacherNoteSubject;
  onClose: () => void;
  onSave: (subject: StartingTeacherNoteSubject, text: string, sharedWithCoach: boolean) => void;
}) {
  const { t } = useTranslation();
  const tooltipId = useId();
  const subjectKey = noteSubjectValue(subject);
  const [selected, setSelected] = useState(subjectKey);
  const [text, setText] = useState("");
  const [shared, setShared] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(subjectKey);
    setText("");
    setShared(false);
  }, [open, subjectKey]);

  const parsed = parseNoteSubject(selected);

  return (
    <Modal
      closeLabel={t("common.close")}
      controlId="starting-teacher.note.add"
      onClose={onClose}
      open={open}
      title={t("startingTeacher.addNote")}
    >
      <FieldGroup>
        <Field>
          <FieldLabel htmlFor="starting-teacher-note-subject">{t("startingTeacher.noteSubject")}</FieldLabel>
          <StartingTeacherNoteSubjectField
            id="starting-teacher-note-subject"
            onValueChange={setSelected}
            value={selected}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="starting-teacher-note-text">{t("startingTeacher.noteText")}</FieldLabel>
          <Textarea
            className="min-h-24"
            data-control-id="starting-teacher.note.text"
            id="starting-teacher-note-text"
            onChange={(event) => setText(event.target.value)}
            placeholder={t("startingTeacher.notePlaceholder")}
            value={text}
          />
        </Field>
        <Field className="flex-row items-center" orientation="horizontal">
          <Switch
            checked={shared}
            data-control-id="starting-teacher.note.share"
            id="starting-teacher-note-share"
            onCheckedChange={setShared}
          />
          <span className="flex min-w-0 items-center gap-1.5">
            <FieldLabel htmlFor="starting-teacher-note-share">{t("startingTeacher.noteShare")}</FieldLabel>
            <span className="group/tip relative">
              <button
                aria-describedby={tooltipId}
                className="grid size-5 place-items-center rounded-full text-muted-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/25"
                data-control-id="starting-teacher.note.share.tooltip"
                type="button"
              >
                <CircleHelp aria-hidden="true" className="size-3.5" />
                <span className="sr-only">{t("startingTeacher.noteShareHint")}</span>
              </button>
              <span
                className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-max max-w-56 -translate-x-1/2 rounded-xl border border-border/60 bg-background px-3 py-2 text-xs leading-relaxed text-foreground opacity-0 shadow-md transition-opacity group-hover/tip:opacity-100 group-focus-within/tip:opacity-100"
                id={tooltipId}
                role="tooltip"
              >
                {t("startingTeacher.noteSharePrivate")}
              </span>
            </span>
          </span>
        </Field>
      </FieldGroup>
      <DialogFooter>
        <Button controlId="starting-teacher.note.cancel" onClick={onClose} type="button" variant="secondary">
          {t("common.cancel")}
        </Button>
        <Button
          controlId="starting-teacher.note.save"
          disabled={!parsed || !text.trim()}
          onClick={() => {
            if (!parsed || !text.trim()) return;
            onSave(parsed, text, shared);
          }}
          type="button"
        >
          {t("startingTeacher.saveNote")}
        </Button>
      </DialogFooter>
    </Modal>
  );
}
